import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import './LoginPage.css';
import { ArrowRight, LogIn } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ErrorModal from '../Common/ErrorModal';
import InfoModal from '../Common/InfoModal';
import adminAuthService from '../../services/adminAuthService';
import easyAuctionLogo from '../../assets/easyauction.png';

interface ModalState {
  isOpen: boolean;
  title: string;
  message: string;
}

interface ErrorModalState extends ModalState {
  showRegisterButton: boolean;
}

const LoginPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [errorModal, setErrorModal] = useState<ErrorModalState>({
    isOpen: false,
    title: 'Error',
    message: '',
    showRegisterButton: false
  });
  const [infoModal, setInfoModal] = useState<ModalState>({
    isOpen: false,
    title: 'Information',
    message: ''
  });

  const { login } = useAuth();
  const navigate = useNavigate();

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  // Email input handler
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value.replace(/\s/g, '').slice(0, 254));
  };

  const closeErrorModal = () => setErrorModal(prev => ({ ...prev, isOpen: false }));
  
  const closeInfoModal = () => {
    setInfoModal(prev => ({ ...prev, isOpen: false }));
    setTimeout(() => {
      navigate('/verify-otp');
    }, 100);
  };

  const handleRegisterClick = () => {
    closeErrorModal();
    navigate('/register');
  };

  // Log modal state changes for debugging
  React.useEffect(() => {
  }, [errorModal]);

  // Form submit handler
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Validation
    if (!email.trim()) {
      setErrorModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Email is required',
        showRegisterButton: false
      });
      return;
    }

    if (!isValidEmail(email)) {
      setErrorModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please enter a valid email address',
        showRegisterButton: false
      });
      return;
    }

    const cleanEmail = email.trim().toLowerCase();

    // Admin login check -- ask the backend (the single source of truth for
    // which emails are admins) instead of hardcoding them here. Any email
    // not recognised as admin falls through to regular user login below.
    const attemptRegularLogin = () => {
      setIsLoading(true);

      login(cleanEmail)
        .then(loginResult => {

          if (loginResult && loginResult.success) {
            setIsLoading(false);
            localStorage.setItem('pendingEmail', cleanEmail);
            localStorage.setItem('isAdminLogin', 'false');
            navigate('/verify-otp');
          } else {
            setIsLoading(false);
            console.error('[LoginPage] Login failed - not successful');
            throw new Error(loginResult?.message || 'Failed to send OTP');
          }
        })
        .catch((error: any) => {
          console.error('[LoginPage] CATCH BLOCK - Login error:', error);
          setIsLoading(false);

          const errorMessage = error?.message || 'Failed to send OTP';

          // Check if user not found
          const isUserNotFound =
            errorMessage.toLowerCase().includes('user not found') ||
            errorMessage.toLowerCase().includes('account not found') ||
            errorMessage.toLowerCase().includes('not registered') ||
            errorMessage.toLowerCase().includes('no user found') ||
            errorMessage.toLowerCase().includes('please sign up');


          if (isUserNotFound) {
            setTimeout(() => {
              setErrorModal({
                isOpen: true,
                title: 'Account Not Found',
                message: 'No account exists with this email. Would you like to create a new account?',
                showRegisterButton: true
              });
            }, 100);
          } else {
            setTimeout(() => {
              setErrorModal({
                isOpen: true,
                title: 'Login Failed',
                message: `Unable to send OTP: ${errorMessage}`,
                showRegisterButton: false
              });
            }, 100);
          }

          // STOP all execution - do NOT navigate
          return false;
        });
    };

    setIsLoading(true);
    adminAuthService.sendAdminOTP({ email: cleanEmail })
      .then(response => {
        if (response.success) {
          setIsLoading(false);
          localStorage.setItem('pendingEmail', cleanEmail);
          if (response.maskedEmail) localStorage.setItem('pendingOtpEmail', response.maskedEmail);
          localStorage.setItem('isAdminLogin', 'true');
          if (response.sessionId) {
            localStorage.setItem('adminSessionId', response.sessionId);
          }
          navigate('/verify-otp');
        } else {
          attemptRegularLogin();
        }
      })
      .catch((err: any) => {
        // A throttled resend must not silently fall through to user login
        if (/wait a few seconds/i.test(err?.message || '')) {
          setIsLoading(false);
          setErrorModal({ isOpen: true, title: 'Please wait', message: err.message, showRegisterButton: false });
          return;
        }
        // Not a recognised admin email (404 "Admin not found") -- try
        // regular user login instead of surfacing this as an error.
        attemptRegularLogin();
      });
  };

  return (
    <div className="ap-login-wrapper" onClick={(e) => e.stopPropagation()}>
      <div className="ap-login-card">
        <div className="ap-login-header">
          <div className="ap-login-logo-row">
            <div className="ap-login-logo-badge">
              <img src={easyAuctionLogo} alt="EasyEAuction logo" className="ap-login-logo-img" />
            </div>
            <div className="ap-login-brand-sub">Welcome back! Please log in to continue</div>
          </div>
        </div>

        <div className="ap-login-inner">
          <div className="ap-login-form-header">
            <h2 className="ap-login-form-title">
              <LogIn className="ap-login-form-icon" />
              Login
            </h2>
            <p className="ap-login-form-subtitle">Enter your email to receive an OTP</p>
          </div>

          <div className="ap-login-form-body">
            <div className="ap-login-form">
              <div className="ap-login-form-group">
                <label htmlFor="email" className="ap-login-label">
                  Email<span style={{ color: "red" }}>*</span>
                </label>
                <div className="ap-login-input-wrapper">
                  <input
                    type="email"
                    id="email"
                    className="ap-login-input"
                    style={{ paddingLeft: 16 }}
                    placeholder="Enter your email"
                    value={email}
                    onChange={handleEmailChange}
                    autoComplete="email"
                    inputMode="email"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isValidEmail(email) && !isLoading) {
                          onSubmit(e as any);
                        }
                      }
                    }}
                  />
                </div>
                <small className="ap-login-hint">We'll email a 4-digit OTP to this address</small>
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSubmit(e);
                }}
                disabled={isLoading || !isValidEmail(email)}
                className="ap-login-btn"
              >
                {isLoading ? (
                  <div className="ap-login-spinner" />
                ) : (
                  <>
                    Send OTP
                    <ArrowRight className="ap-login-btn-icon" />
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="ap-login-footer">
            <p className="ap-login-footer-text">
              New here?{' '}
              <Link to="/register" className="ap-login-footer-link">
                Create an account
              </Link>
            </p>
          </div>
        </div>

        <div className="ap-login-backhome">
          <Link to="/" className="ap-login-backhome-link">
            <span>Back to Home</span>
          </Link>
        </div>
      </div>

      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={closeErrorModal}
        title={errorModal.title}
        message={errorModal.message}
        showRegisterButton={errorModal.showRegisterButton}
        onRegisterClick={handleRegisterClick}
      />

      <InfoModal
        isOpen={infoModal.isOpen}
        onClose={closeInfoModal}
        title={infoModal.title}
        message={infoModal.message}
      />
    </div>
  );
};

export default LoginPage;


